import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/utils/supabase';
import { toast } from '@/hooks/use-toast';

export interface Partner {
  id: number;
  name: string;
  logo_url: string;
  logo_path?: string;
  created_at?: string;
}

interface DeletePartnerResult {
  success: boolean;
  error?: any;
  deletedFromLocalOnly?: boolean;
}

interface SavePartnersResult {
  success: boolean;
  error?: any;
  savedToLocalOnly?: boolean;
  message?: string;
  errorDetails?: string;
}

interface UploadResult {
  url: string;
  path: string;
  error?: any;
}

export const usePartners = (isInitialized: boolean) => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const lastFetchTimeRef = useRef<number>(0);
  const fetchCountRef = useRef<number>(0);
  const fetchInProgressRef = useRef<boolean>(false);
  const consecutiveErrorsRef = useRef<number>(0);
  const backoffTimeRef = useRef<number>(1000); // Start with 1 second
  const abortControllerRef = useRef<AbortController | null>(null);
  const timeoutIdRef = useRef<number | null>(null);
  
  const loadPartners = useCallback(async (forceRefresh = false) => {
    try {
      if (fetchInProgressRef.current) {
        console.log("Partner load already in progress, skipping request");
        return partners;
      }
      
      const now = Date.now();
      if (!forceRefresh && now - lastFetchTimeRef.current < 30000) {
        console.log("Partners fetch throttled - too frequent");
        return partners;
      }
      
      if (fetchCountRef.current > 3 && !forceRefresh) {
        console.log("Partners fetch count exceeded, using cached data");
        return partners;
      }
      
      setIsLoading(true);
      fetchInProgressRef.current = true;
      lastFetchTimeRef.current = now;
      fetchCountRef.current++;
      
      console.log("Loading partners...");
      const items = await getPartners();
      
      fetchInProgressRef.current = false;
      setPartners(items || []);
      
      consecutiveErrorsRef.current = 0;
      backoffTimeRef.current = 1000;
      
      return items;
    } catch (error) {
      console.error("Error loading partners:", error);
      fetchInProgressRef.current = false;
      
      consecutiveErrorsRef.current++;
      if (consecutiveErrorsRef.current > 1) {
        backoffTimeRef.current = Math.min(backoffTimeRef.current * 2, 60000);
        console.log(`Backing off partner fetches for ${backoffTimeRef.current}ms due to consecutive errors`);
      }
      
      return partners || [];
    } finally {
      setIsLoading(false);
    }
  }, [partners]);
  
  const savePartners = async (partnersData: Partner[], isAdmin: boolean = false): Promise<SavePartnersResult> => {
    try {
      localStorage.setItem('partnerLogos', JSON.stringify(partnersData));
      
      if (!isInitialized) {
        console.warn('Supabase not initialized, saving only to localStorage');
        return { success: true };
      }

      if (!isAdmin && !import.meta.env.DEV) {
        console.warn('User not authorized to save partners');
        return { success: false, error: 'Not authorized' };
      }

      const partnersToUpsert = partnersData.map(partner => ({
        id: partner.id,
        name: partner.name,
        logo_url: partner.logo_url,
        logo_path: partner.logo_path || null,
        created_at: partner.created_at || new Date().toISOString()
      }));
      
      try {
        // Clean up any existing timeout before setting a new one
        cleanupRequestResources();
        
        // Create a new abort controller for this request
        abortControllerRef.current = new AbortController();
        
        // Set up a timeout to abort the request after 5 seconds
        timeoutIdRef.current = setTimeout(() => {
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
          }
        }, 5000) as unknown as number;
        
        const { error } = await supabase
          .from('partners')
          .upsert(partnersToUpsert, { 
            onConflict: 'id'
          });
        
        // Clear resources after the request completes
        cleanupRequestResources();
        
        if (error) {
          if (error.message && error.message.includes('row-level security policy')) {
            console.warn('RLS policy preventing partner save, still saved to localStorage');
            return { 
              success: true, 
              savedToLocalOnly: true,
              message: 'Saved to local storage only due to RLS restrictions'
            };
          }
          
          throw error;
        }
        
        return { success: true };
      } catch (e: any) {
        console.error('Error in Supabase partners upsert:', e);
        
        const isAbortError = e.name === 'AbortError' || 
                            (e.message && e.message.includes('aborted'));
        
        if (isAbortError) {
          console.log("Save operation timed out, saved to localStorage only");
        }
        
        return { 
          success: true, 
          savedToLocalOnly: true,
          errorDetails: e.message 
        };
      }
    } catch (e: any) {
      console.error('Error in savePartners:', e);
      return { 
        success: true, 
        errorDetails: e.message 
      };
    }
  };

  // Safety cleanup function to remove any pending abort controllers and timeouts
  const cleanupRequestResources = () => {
    if (abortControllerRef.current) {
      try {
        abortControllerRef.current.abort();
      } catch (e) {
        // Ignore abort errors
      }
      abortControllerRef.current = null;
    }
    
    if (timeoutIdRef.current !== null) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  };

  const getPartners = async (): Promise<Partner[]> => {
    try {
      // Always try to load from local storage first
      const storedPartners = localStorage.getItem('partnerLogos');
      let localPartners: Partner[] = [];
      
      if (storedPartners) {
        try {
          localPartners = JSON.parse(storedPartners);
          console.log("Loaded", localPartners.length, "partners from localStorage");
        } catch (e) {
          console.warn('Failed to parse stored partners from localStorage');
        }
      }
      
      // If not initialized or too many errors, just return local data
      if (!isInitialized || consecutiveErrorsRef.current > 3) {
        console.log("Using local storage only for partners due to connectivity issues");
        return localPartners;
      }

      try {
        // Clean up any previous abort controller and timeout
        cleanupRequestResources();
        
        // Create a new controller for this request
        abortControllerRef.current = new AbortController();
        
        // Use a more resilient timeout approach
        const fetchTimeout = 5000; // 5s timeout
        timeoutIdRef.current = setTimeout(() => {
          if (abortControllerRef.current) {
            try {
              abortControllerRef.current.abort();
              abortControllerRef.current = null;
            } catch (e) {
              // Ignore errors when aborting
            }
          }
        }, fetchTimeout) as unknown as number;
        
        const { data: fetchData, error: fetchError } = await supabase
          .from('partners')
          .select('*')
          .abortSignal(abortControllerRef.current.signal);
          
        // Clear the timeout once the request completes
        cleanupRequestResources();
        
        if (fetchError) {
          console.warn("Error fetching partners from Supabase:", fetchError);
          
          if (fetchError.message && fetchError.message.includes('row-level security policy')) {
            console.log("RLS policy preventing partners fetch, using localStorage data");
            return localPartners;
          }
  
          console.log("Error fetching partners from Supabase, using localStorage data");
          return localPartners;
        }
        
        if (!fetchData || fetchData.length === 0) {
          console.log("No partners found in Supabase, using localStorage data");
          return localPartners;
        }
        
        const partnersData = fetchData.map((partner: any) => ({
          id: partner.id,
          name: partner.name,
          logo_url: partner.logo_url,
          logo_path: partner.logo_path || null
        }));
        
        console.log("Successfully fetched", partnersData.length, "partners from Supabase");
        localStorage.setItem('partnerLogos', JSON.stringify(partnersData));
        return partnersData;
      } catch (err: any) {
        // Handle abort errors more gracefully
        if (err.name === 'AbortError' || (err.message && err.message.includes('AbortError'))) {
          console.warn('Partners fetch timed out, using localStorage data');
        } else {
          console.warn('Error fetching partners from Supabase, using localStorage:', err);
        }
        
        // Increment consecutive errors on timeout
        consecutiveErrorsRef.current++;
        return localPartners;
      } finally {
        // Clean up resources in finally block to ensure they're always cleaned up
        cleanupRequestResources();
      }
    } catch (err: any) {
      console.warn('Error getting partners, returning empty array:', err);
      return [];
    }
  };

  const deletePartner = async (partnerId: number, isAdmin: boolean = false): Promise<DeletePartnerResult> => {
    try {
      if (!isAdmin && !import.meta.env.DEV) {
        console.warn('User not authorized to delete partner');
        return { success: false, error: 'Not authorized' };
      }
      
      const storedPartners = localStorage.getItem('partnerLogos');
      if (storedPartners) {
        const currentPartners: Partner[] = JSON.parse(storedPartners);
        const updatedPartners = currentPartners.filter(partner => partner.id !== partnerId);
        localStorage.setItem('partnerLogos', JSON.stringify(updatedPartners));
      }
      
      if (!isInitialized) {
        console.warn('Supabase not initialized, using localStorage for deletion only');
        return { success: true };
      }
      
      try {
        // Clean up any previous abort controller and timeout
        cleanupRequestResources();
        
        // Create a new controller for this request
        abortControllerRef.current = new AbortController();
        
        // Use a more resilient timeout approach
        timeoutIdRef.current = setTimeout(() => {
          if (abortControllerRef.current) {
            try {
              abortControllerRef.current.abort();
              abortControllerRef.current = null;
            } catch (e) {
              // Ignore errors when aborting
            }
          }
        }, 5000) as unknown as number;
        
        const { error } = await supabase
          .from('partners')
          .delete()
          .eq('id', partnerId)
          .abortSignal(abortControllerRef.current.signal);
        
        // Clear resources after the request completes
        cleanupRequestResources();
        
        if (error) {
          if (error.message && error.message.includes('row-level security policy')) {
            console.log('RLS policy preventing partner deletion in Supabase, still deleted from localStorage');
            return { 
              success: true, 
              deletedFromLocalOnly: true
            };
          }
          
          throw error;
        }
      } catch (err: any) {
        // Handle abort errors more gracefully
        const isAbortError = err.name === 'AbortError' || 
                          (err.message && err.message.includes('AbortError'));
        
        if (isAbortError) {
          console.warn('Partner delete operation timed out (but deleted from localStorage)');
        } else {
          console.warn('Error deleting partner from Supabase (but deleted from localStorage):', err);
        }
        
        return { 
          success: true, 
          deletedFromLocalOnly: true,
          error: err 
        };
      }
      
      return { success: true };
    } catch (err) {
      console.warn('Error deleting partner:', err);
      return { success: false, error: err };
    }
  };
  
  // Helper method to upload files directly from the hook
  const uploadFile = async (file: File, bucket: string, fileName?: string): Promise<UploadResult> => {
    try {
      // Create a local data URL for the file as backup
      const localUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });
      
      // First try to save locally
      const localPath = `local_${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
      try {
        const localFiles = JSON.parse(localStorage.getItem('localFiles') || '{}');
        localFiles[localPath] = {
          url: localUrl,
          name: file.name,
          type: file.type,
          bucket,
          createdAt: new Date().toISOString()
        };
        localStorage.setItem('localFiles', JSON.stringify(localFiles));
      } catch (e) {
        console.warn("Could not save file to localStorage", e);
      }
      
      if (!isInitialized) {
        console.log("Supabase not initialized, using local storage only for file upload");
        return {
          url: localUrl,
          path: localPath
        };
      }
      
      // Then try Supabase
      try {
        // Clean up any previous resources
        cleanupRequestResources();
        
        // Create a new abort controller for this request
        abortControllerRef.current = new AbortController();
        
        // Use a more resilient timeout approach - longer for uploads
        const uploadTimeout = 10000; // 10s timeout for uploads
        timeoutIdRef.current = setTimeout(() => {
          if (abortControllerRef.current) {
            try {
              abortControllerRef.current.abort();
              abortControllerRef.current = null;
            } catch (e) {
              // Ignore errors when aborting
            }
          }
        }, uploadTimeout) as unknown as number;
        
        const actualFileName = fileName || `${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
        
        console.log(`Uploading to bucket ${bucket}:`, actualFileName);
        
        const { data, error } = await supabase.storage
          .from(bucket)
          .upload(actualFileName, file, {
            cacheControl: '3600',
            upsert: true,
          });
          
        // Clean up resources
        cleanupRequestResources();
        
        if (error) {
          console.error("Upload error:", error);
          
          // If this is an RLS policy error or bucket doesn't exist, return the local URL
          // Update: StorageError doesn't have statusCode property, check for message contents or status
          if (error.message.includes('row-level security policy') || 
              error.message.includes('The bucket') || 
              error.message.includes('400')) {
            console.log("Storage error - using local storage for file instead");
            return {
              url: localUrl,
              path: localPath,
              error
            };
          }
          
          throw error;
        }
        
        const { data: publicUrlData } = supabase.storage
          .from(bucket)
          .getPublicUrl(data.path);
        
        return {
          url: publicUrlData.publicUrl,
          path: data.path
        };
      } catch (err: any) {
        console.error("Upload exception:", err);
        
        // For any error, fall back to the local URL
        return {
          url: localUrl,
          path: localPath,
          error: err
        };
      }
    } catch (err) {
      console.error("Overall upload error:", err);
      throw err;
    }
  };

  // Cleanup function to be called when the component unmounts
  const cleanup = () => {
    cleanupRequestResources();
  };

  return {
    partners,
    isLoading,
    savePartners,
    getPartners,
    deletePartner,
    loadPartners,
    uploadFile,
    cleanup
  };
};
